import { GRID_LAYERS, ORIENTATION } from "./GridModel.js";
import { BLANK_CHAR } from "./GridTile.js";

export class GameController {
  /**
   * @param {FinModel} finModel - The fin model instance.
   * @param {GameView} gameView - The view responsible for rendering the canvas.
   * @param {GridModel} gridModel - The grid model that parses the fin model.
   * @param {FinGame} finGame - Optional reference to FinGame for progress updates.
   */
  constructor(finModel, gameView, gridModel, finGame = null) {
    this.finModel = finModel;
    this.gameView = gameView;
    this.gridModel = gridModel;
    this.finGame = finGame;
    this.active = false;
  }

  /**
   * Initializes the game by parsing the fin model and rendering the grid.
   */
  init() {
    // Parse the fin model to create the grid matrix
    this.gridModel.parseFinModel(this.finModel);
    // Render the initial grid on the canvas
    this.drawGameView();
  }

  /**
   * Renders the game view by getting the grid matrix and updating the canvas.
   * Shows selection highlight only when controller is active.
   *
   * @returns {void}
   */
  drawGameView() {
    const matrix = this.gridModel.getMatrix();
    const selectedTile = this.gridModel.getSelectedTile();
    if (this.active) {
      this.gameView.render(
        matrix,
        selectedTile,
        this.gridModel.currentOrientation,
      );
    } else {
      this.gameView.render(matrix, null, this.gridModel.currentOrientation);
    }
  }

  /**
   * Starts the game by setting up the starting word and switching to playing layer.
   * Reveals all letters in the starting word and updates progress.
   *
   * @returns {void}
   */
  doStart() {
    const theStartingWord = this.gridModel.getStartGridWord();
    this.gridModel.setCurrentLayer(GRID_LAYERS.PLAYING);
    this.gridModel.setSelectionToWord(theStartingWord);
    this.revealStartWord(theStartingWord);
    this.drawGameView();
    // Update progress to reflect starting word tiles
    if (this.finGame && typeof this.finGame.updateProgress === "function") {
      this.finGame.updateProgress();
    }
  }

  /**
   * Reveals all letters in the starting word by setting guess characters to solution values.
   *
   * @param {GridModelWord} startWord - The starting word to reveal
   * @returns {void}
   */
  revealStartWord(startWord) {
    if (startWord && Array.isArray(startWord.tiles)) {
      const tiles = startWord.getTiles();
      for (let index = 0; index < tiles.length; index++) {
        const tile = tiles[index];
        const solution = tile.getSolutionValue();
        tile.setGuessChar(solution);
      }
    }
  }

  /**
   * Solves the currently selected word by revealing all its letters.
   * Updates progress and redraws the game view.
   *
   * @returns {void}
   */
  doSolveWord() {
    const theSelectedWord = this.gridModel.getSelectedWord();
    console.log("doSolveWord: GameController", theSelectedWord);
    if (theSelectedWord && Array.isArray(theSelectedWord.tiles)) {
      // this.gridModel.solveSelectedWord();
      const tiles = theSelectedWord.getTiles();
      for (let index = 0; index < tiles.length; index++) {
        const tile = tiles[index];
        const solution = tile.getSolutionValue();
        tile.setRevealedChar(solution);
      }

      this.drawGameView();
      if (this.finGame && typeof this.finGame.updateProgress === "function") {
        this.finGame.updateProgress();
      }
    }
  }

  /**
   * Solves the currently selected tile by revealing its correct letter.
   * Updates progress and redraws the game view.
   *
   * @returns {void}
   */
  doSolveTile() {
    const theSelectedTile = this.gridModel.getSelectedTile(); // this is a bit overkill the solver does this
    console.log("doSolveTile: GameController", theSelectedTile);
    if (theSelectedTile) {
      this.gridModel.solveSelectedTile();
      this.drawGameView();
      if (this.finGame && typeof this.finGame.updateProgress === "function") {
        this.finGame.updateProgress();
      }
    }
  }

  /**
   * Clears the guess character from the currently selected tile.
   * Updates progress and redraws the game view.
   *
   * @returns {void}
   */
  doClearTile() {
    const theSelectedTile = this.gridModel.getSelectedTile();
    console.log("doClearTile: GameController", theSelectedTile);
    if (theSelectedTile) {
      theSelectedTile.removeGuessChar();
      this.drawGameView();
      if (this.finGame && typeof this.finGame.updateProgress === "function") {
        this.finGame.updateProgress();
      }
    }
  }

  /**
   * Checks if the currently selected tile's guess matches the solution.
   *
   * @returns {boolean} True if the tile's playing value matches the solution
   */
  doCheckTile() {
    const theSelectedTile = this.gridModel.getSelectedTile();
    console.log("doCheckTile: GameController", theSelectedTile);
    if (theSelectedTile) {
      const solutionLetter = theSelectedTile.getSolutionValue();
      const playingLetter = theSelectedTile.getPlayingValue();
      if (solutionLetter == playingLetter) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks if the currently selected word is completely and correctly filled.
   *
   * @returns {boolean} True if all tiles in the word match their solutions
   */
  doCheckWord() {
    const theSelectedWord = this.gridModel.getSelectedWord();
    console.log("doCheckWord: GameController", theSelectedWord);
    const tiles = theSelectedWord.getTiles();
    if (theSelectedWord && Array.isArray(tiles)) {
      for (let i = 0; i < tiles.length; i++) {
        const tile = tiles[i];
        const playValue = tile.getPlayingValue();
        if (playValue == BLANK_CHAR || tile.getSolutionValue() !== playValue) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Clears all guess characters from the currently selected word.
   * Updates progress and redraws the game view.
   *
   * @returns {void}
   */
  doClearWord() {
    const theSelectedWord = this.gridModel.getSelectedWord();
    console.log("doClearWord: GameController", theSelectedWord);
    if (theSelectedWord && Array.isArray(theSelectedWord.tiles)) {
      this.gridModel.clearSelectedWord();
      this.drawGameView();
      if (this.finGame && typeof this.finGame.updateProgress === "function") {
        this.finGame.updateProgress();
      }
    }
  }

  /**
   * Solves the entire puzzle by revealing all across words.
   * Redraws the game view after completion.
   *
   * @returns {void}
   */
  doSolvePuzzle() {
    const acrossWords = this.gridModel.getAcrossGridWords();
    for (const word of acrossWords) {
      this.gridModel.solveWord(word);
    }
    this.drawGameView();
  }

  /**
   * Resets the puzzle by clearing all guess characters from all words.
   * Redraws the game view after reset.
   *
   * @returns {void}
   */
  doReset() {
    console.log("Do Reset GameController");
    const acrossWords = this.gridModel.getAcrossGridWords();
    //FIXME: Maybe not all words need to be cleared, maybe all tiles should be cleared.
    for (const word of acrossWords) {
      if (typeof this.gridModel.clearWord === "function") {
        this.gridModel.clearWord(word);
      }
    }
    this.drawGameView();
  }

  /**
   * Handles clicks on grid cells, updating selection and toggling orientation.
   * If the same cell is clicked, toggles between ACROSS and DOWN orientation.
   *
   * @param {number} row - 0-based row index of clicked cell
   * @param {number} column - 0-based column index of clicked cell
   * @returns {void}
   */
  handleCellClick(row, column) {
    const currentSelection = this.gridModel.getCurrentSelection();
    // Check if the clicked cell is already selected
    if (
      currentSelection &&
      currentSelection.row === row &&
      currentSelection.col === column
    ) {
      // Toggle orientation
      const currentOrientation =
        this.gridModel.currentOrientation === ORIENTATION.ACROSS
          ? ORIENTATION.DOWN
          : ORIENTATION.ACROSS;
      this.gridModel.currentOrientation = currentOrientation;

      // Redraw the view with the same selected cell and new orientation
      this.drawGameView();
      return;
    }

    // Otherwise, update the selection and redraw as usual
    this.gridModel.setCurrentSelection(row, column);
    const matrix = this.gridModel.getMatrix();
    const selection = this.gridModel.getCurrentSelection();
    const selectedTile = this.gridModel.getTileAtLocation(selection);
    // this.gameView.render(matrix, selectedTile, this.gridModel.getCurrentLayer(), this.gridModel.currentOrientation);
    this.gameView.render(
      matrix,
      selectedTile,
      this.gridModel.currentOrientation,
    );
  }

  /**
   * Handles mouse clicks on the game canvas, converting coordinates to grid cells.
   * Accounts for canvas scaling and calls handleCellClick with the calculated position.
   *
   * @param {MouseEvent} event - The mouse click event
   * @returns {void}
   */
  handleCanvasClick(event) {
    if (!this.active) return;
    const canvas = event.target;
    const rect = canvas.getBoundingClientRect();

    // Mouse position relative to canvas in viewport coordinates
    const viewportX = event.clientX - rect.left;
    const viewportY = event.clientY - rect.top;

    // Account for canvas scaling when it's displayed smaller than its actual size
    // The canvas may be scaled down due to CSS (object-fit: contain, max-width, etc.)
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Convert viewport coordinates to canvas coordinates
    const x = viewportX * scaleX;
    const y = viewportY * scaleY;

    // Assuming you know the grid size (rows, cols) and canvas size
    const cellSize = this.gameView.cellSize;

    // Calculate row and column
    const col = Math.floor(x / cellSize);
    const row = Math.floor(y / cellSize);

    // Now you have the cell indices!
    this.handleCellClick(row, col);
  }

  /**
   * Handles keyboard input for navigation, character entry, and game actions.
   * Supports arrow keys, tab navigation, backspace/delete, space for orientation toggle, and character input.
   *
   * @param {KeyboardEvent} event - The keyboard event containing key information
   * @returns {void}
   */
  handleEvent(event) {
    if (!this.active) return;
    // Implement event handling logic as needed
    // Example: update gridModel, then re-render
    if (
      ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)
    ) {
      event.preventDefault();
      // ...handle arrow key logic...
      if (typeof this.gridModel.update === "function") {
        this.gridModel.update(event);

        this.drawGameView();
      } else {
        console.warn(
          "Unknown event type or update method not implemented:",
          event,
        );
      }
      return;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      let gm = this.gridModel;
      const aTile = gm.getSelectedTile();
      const co = gm.getCurrentOrientation();

      // Get the list of words for the current orientation
      const words =
        co === ORIENTATION.ACROSS ? gm.acrossGridWords : gm.downGridWords;
      if (!words || words.length === 0) return;

      // Find the current word index
      const currentWord =
        co === ORIENTATION.ACROSS ? aTile.getAcrossWord() : aTile.getDownWord();
      const idx = words.indexOf(currentWord);

      // Get the next word, wrap around if at the end
      let nextWord;
      if (event.shiftKey) {
        // Go to previous word, wrap around if at the start
        nextWord = words[(idx - 1 + words.length) % words.length];
      } else {
        // Go to next word, wrap around if at the end
        nextWord = words[(idx + 1) % words.length];
      }

      // Set selection to the first tile of the next word
      if (nextWord && typeof gm.setSelectionToWord === "function") {
        gm.setSelectionToWord(nextWord);
      }
      this.drawGameView();
      return;
    }

    // Handle orientation change (Space bar)
    if (event.code === "Space") {
      event.preventDefault();
      const gm = this.gridModel;
      const aTile = gm.getSelectedTile();
      const co = gm.getCurrentOrientation();
      if (co === ORIENTATION.ACROSS) {
        const aWord = aTile.getDownWord();
        gm.setSelectionToWord(aWord);
      } else {
        const aWord = aTile.getAcrossWord();
        gm.setSelectionToWord(aWord);
      }

      // Re-render the grid with new orientation
      this.drawGameView();
      return;
    }

    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      const gm = this.gridModel;
      const selectedTile = gm.getSelectedTile();
      if (!selectedTile) return;

      if (
        selectedTile.getPlayingValue &&
        selectedTile.getPlayingValue() !== " "
      ) {
        // Erase the guess character, do not move cursor
        selectedTile.removeGuessChar();
      } else {
        // Retreat cursor, then erase at new location
        if (typeof gm.retreatCursor === "function") {
          gm.retreatCursor();
          const newTile = gm.getSelectedTile();
          if (newTile && newTile.removeGuessChar) {
            newTile.removeGuessChar();
          }
        }
      }
      this.drawGameView();
      if (this.finGame && typeof this.finGame.updateProgress === "function") {
        this.finGame.updateProgress();
      }
      return;
    }

    // Get puzzle type from FinModel to determine valid input
    const puzzleType = this.finModel?.getType()?.toUpperCase() || "ALPHA";
    console.log(`[GameController] Puzzle type: ${puzzleType}`);

    // Handle character input based on puzzle type
    let inputChar = null;

    if (puzzleType === "ALPHA" || puzzleType === "ALPHANUMERIC") {
      // Handle alpha keys (A-Z, a-z)
      if (/^[a-zA-Z]$/.test(event.key)) {
        event.preventDefault();
        inputChar = event.key.toUpperCase();
      }
    }

    if (
      puzzleType === "NUMBER" ||
      puzzleType === "NUMERIC" ||
      puzzleType === "ALPHANUMERIC"
    ) {
      // Handle numeric keys (0-9)
      if (/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        inputChar = event.key; // Keep as-is (no need to uppercase numbers)
      }
    }

    // If we have valid input, process it
    if (inputChar !== null) {
      const selection = this.gridModel.getCurrentSelection();
      if (selection) {
        let selectedTile = this.gridModel.getSelectedTile();
        selectedTile.setGuessChar(inputChar);

        // Optionally advance cursor after entry
        if (typeof this.gridModel.advanceCursor === "function") {
          this.gridModel.advanceCursor();
        }

        this.drawGameView();
        if (this.finGame && typeof this.finGame.updateProgress === "function") {
          this.finGame.updateProgress();
        }
      }
      return;
    }
  }
  /**
   * Sets the responsive display mode for the game view.
   * Updates cell sizing and forces immediate redraw.
   *
   * @param {string} mode - The responsive mode (e.g., 'desktop', 'tablet', 'mobile')
   * @returns {void}
   */
  setResponsiveMode(mode) {
    console.log("[GameController] setResponsiveMode:", mode);
    if (this.gameView?.setResponsiveMode) {
      this.gameView.setResponsiveMode(mode);
      // Force immediate redraw to apply new cell size
      this.drawGameView();
    }
  }

  /**
   * Handles clicks on clue list items from the clue boards.
   * Finds the corresponding grid word and sets selection to it.
   *
   * @param {string} clueText - The word value to find and select in the grid
   * @returns {void}
   */
  handleClueWordClick(clueText) {
    // Find the corresponding GridModelWord by matching word value
    const gridWord =
      this.gridModel.getGridWordByGuessValue(clueText, ORIENTATION.ACROSS) ||
      this.gridModel.getGridWordByGuessValue(clueText, ORIENTATION.DOWN);

    if (gridWord) {
      console.log("[GameController] Grid word, selecting it:", gridWord);
      this.gridModel.setSelectionToWord(gridWord);
      this.drawGameView();

      // Focus the canvas so keyboard input works immediately
      if (this.gameView && this.gameView.canvas) {
        this.gameView.canvas.focus();
      }
    }
  }

  /**
   * Activates the game controller to respond to user input.
   *
   * @returns {void}
   */
  activate() {
    this.active = true;
  }

  /**
   * Deactivates the game controller, disabling user input handling.
   *
   * @returns {void}
   */
  deactivate() {
    this.active = false;
  }
}
